import { faFileCsv, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    Card,
    CardContent,
    Grid,
    IconButton,
    Tooltip as MuiTooltip,
    Skeleton,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { Theme } from '@mui/system';
import dinero from 'dinero.js';
import { parse as parseCsv } from 'json2csv';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import * as XLSX from 'xlsx';
import { OnlyFansMetricsSubscribedAtVsTotalSalesDetailsDataPoint } from '../../../../../../hooks/useSextforceMetricsSubscribedAtVsTotalSalesDetails';
import { d2f } from '../../../../../../utils/common';

interface TransformedDataPoint {
    subscribedAt: string;
    count: number;
}

interface ChartDataPoint {
    time: number;
    count: number;
    total: number;
    displayTime?: string; // For display purposes
}

interface ExportData {
    headers: { label: string; value: string }[];
    rows: TransformedDataPoint[];
}

interface CustomTooltipProps {
    active?: boolean;
    payload?: any[];
    showEarningsAsGross: boolean;
    showEarningsWithSubscriptions: boolean;
    timezone: string;
    theme: Theme;
}

/**
 * Transforms the transactions data rows from the server to a format ready for export to CSV or Excel
 * @param rows Transactions rows
 * @param timezone Timezone string to convert the transaction time to
 * @returns Transformed data for export
 */
const transformTransactionsForExport = (
    rows: OnlyFansMetricsSubscribedAtVsTotalSalesDetailsDataPoint[] | undefined,
    timezone: string,
): ExportData => {
    if (!rows || rows.length === 0) {
        return { headers: [], rows: [] };
    }

    const headers = [
        {
            label: `Subscribed At ${timezone}`,
            value: 'subscribedAt',
        },
        {
            label: 'Fans',
            value: 'count',
        },
    ];

    // Rows without an interval, or whose interval day falls before the campaign was created,
    // cannot be attributed to a date and are reported as "Unknown"
    const isUnknown = (row: OnlyFansMetricsSubscribedAtVsTotalSalesDetailsDataPoint): boolean =>
        !row._id.interval || moment(row._id.interval).tz(timezone).isBefore(moment(row._id.createdAt).tz(timezone), 'day');

    const filteredData = rows.filter(row => !isUnknown(row));

    const data: TransformedDataPoint[] = [];

    if (filteredData.length > 0) {
        // Iterate from the earliest date to the latest date and fill in missing dates with 0 counts.
        // All day arithmetic is done in the requested timezone - the server returns each interval
        // as midnight in that timezone, so comparing days in the browser's local timezone would
        // shift dates for users west of the selected timezone.
        const earliestDate = moment(filteredData[0]._id.interval).tz(timezone);
        const latestDate = moment().tz(timezone);

        for (let date = earliestDate.clone(); date.isBefore(latestDate); date.add(1, 'days')) {
            const foundData = filteredData.find(d => moment(d._id.interval).tz(timezone).isSame(date, 'day'));

            data.push({
                subscribedAt: date.format('L'),
                count: foundData ? foundData.count : 0,
            });
        }
    }

    // Add up all the data that could not be attributed to a date
    let unknownCount = 0;

    for (const row of rows) {
        if (isUnknown(row)) {
            unknownCount += row.count;
        }
    }

    if (unknownCount > 0) {
        data.push({
            subscribedAt: 'Unknown',
            count: unknownCount,
        });
    }

    return { headers, rows: data };
};

const generateData = (
    data: OnlyFansMetricsSubscribedAtVsTotalSalesDetailsDataPoint[] | undefined,
    showEarningsWithSubscriptions: boolean,
    showEarningsAsGross: boolean,
    viewByMonth: boolean,
    timezone: string,
): { data: ChartDataPoint[] } => {
    if (!data || data.length === 0) {
        return { data: [] };
    }

    // const dateThreshold = moment(data[0]._id.createdAt).startOf('day');

    // Filter data by date threshold
    const filteredData = data; //.slice().filter(d => dateThreshold.isSameOrBefore(d._id.interval));

    if (viewByMonth) {
        // Group by month when in month view
        const monthlyData: { [key: string]: ChartDataPoint } = {};

        filteredData.forEach(d => {
            let total: number = 0;

            if (showEarningsWithSubscriptions) {
                total = d.total ? (typeof d.total === 'number' ? d.total : d2f(d.total)) : 0;
            } else {
                total = d.totalWithoutSubscriptions
                    ? typeof d.totalWithoutSubscriptions === 'number'
                        ? d.totalWithoutSubscriptions
                        : d2f(d.totalWithoutSubscriptions)
                    : 0;
            }

            if (!showEarningsAsGross) {
                total = total * 0.8;
            }

            // Create month key in format YYYY-MM (in the requested timezone)
            const date = moment(d._id.interval).tz(timezone);
            const monthKey = date.format('YYYY-MM');
            const monthDisplay = date.format('MMM YYYY');

            // Initialize the month entry if it doesn't exist
            if (!monthlyData[monthKey]) {
                monthlyData[monthKey] = {
                    time: date.clone().startOf('month').valueOf(),
                    displayTime: monthDisplay,
                    count: 0,
                    total: 0,
                };
            }

            // Aggregate the data
            monthlyData[monthKey].count += d.count;
            monthlyData[monthKey].total += total;
        });

        // Convert the grouped data object to an array and sort by time
        return {
            data: Object.values(monthlyData).sort((a, b) => a.time - b.time),
        };
    } else {
        // Daily view - similar to the original implementation
        return {
            data: filteredData.map(d => {
                let total: number = 0;

                if (showEarningsWithSubscriptions) {
                    total = d.total ? (typeof d.total === 'number' ? d.total : d2f(d.total)) : 0;
                } else {
                    total = d.totalWithoutSubscriptions
                        ? typeof d.totalWithoutSubscriptions === 'number'
                            ? d.totalWithoutSubscriptions
                            : d2f(d.totalWithoutSubscriptions)
                        : 0;
                }

                if (!showEarningsAsGross) {
                    total = total * 0.8;
                }

                const date = moment(d._id.interval).tz(timezone);

                return {
                    time: new Date(d._id.interval).valueOf(),
                    displayTime: date.format('L'),
                    count: d.count,
                    total,
                };
            }),
        };
    }
};

/**
 * Creates a custom tooltip for the chart
 */
const CustomTooltip = ({ active, payload, showEarningsAsGross, showEarningsWithSubscriptions, timezone, theme }: CustomTooltipProps) => {
    if (active && payload && payload.length) {
        return (
            <div role="tooltip" aria-live="polite">
                <Grid
                    container
                    flexGrow={0}
                    alignItems="center"
                    spacing={1}
                    sx={{
                        backgroundColor: '#fff',
                        borderRadius: 4,
                        border: '1px solid #999',
                        maxWidth: 280,
                    }}
                >
                    <Grid item xs={12}>
                        <span style={{ fontWeight: 'bold' }}>{moment(payload[0].payload.time).tz(timezone).format('L')}</span>
                    </Grid>
                    {payload.map((item: any, index: number) => (
                        <Grid item xs={12} key={index}>
                            {item.dataKey === 'count' && (
                                <Grid container flexGrow={1} alignItems="center">
                                    <Grid item xs={4} sx={{ color: theme.palette.primary.main }}>
                                        Subscribers:
                                    </Grid>
                                    <Grid item xs={8} textAlign={'right'} sx={{ color: theme.palette.primary.main, paddingRight: '8px' }}>
                                        {item.value && typeof item.value === 'number' ? item.value.toLocaleString() : 0}
                                    </Grid>
                                </Grid>
                            )}

                            {item.dataKey === 'total' && (
                                <Grid container flexGrow={1} alignItems="center">
                                    <Grid item xs={4} sx={{ color: theme.palette.secondary.main }}>
                                        Total Sales:
                                    </Grid>
                                    <Grid
                                        item
                                        xs={8}
                                        textAlign={'right'}
                                        sx={{ color: theme.palette.primary.main, paddingRight: '8px', fontFamily: 'monospace' }}
                                    >
                                        {dinero({
                                            amount: item.value ? Math.trunc(item.value * 100) : 0,
                                            currency: 'USD',
                                        }).toFormat()}{' '}
                                        <small>({showEarningsAsGross ? 'gross' : 'net'})</small>
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                    ))}
                </Grid>
            </div>
        );
    }

    return null;
};

interface Props {
    metricId: string;
    promoCampaignSubscribedAtVsSales: OnlyFansMetricsSubscribedAtVsTotalSalesDetailsDataPoint[] | undefined;
    promoCampaignSubscribedAtVsSalesLoading: boolean;
    showEarnings: boolean;
    showEarningsWithSubscriptions: boolean;
    showEarningsAsGross: boolean;
    timezone: string;
    theme: Theme;
}

/**
 * Utility function to handle data export
 */
const exportData = (
    data: OnlyFansMetricsSubscribedAtVsTotalSalesDetailsDataPoint[] | undefined,
    metricId: string,
    timezone: string,
    format: 'csv' | 'excel',
): void => {
    const transformedData: ExportData = transformTransactionsForExport(data, timezone);
    const filename = `subscribersByDateCount_${metricId}_${moment().tz(timezone).format('YYYY-MM-DD')}`;

    if (format === 'csv') {
        // Format JSON data to CSV
        const csv = parseCsv(transformedData.rows, {
            fields: transformedData.headers,
            transforms: [],
        });
        // Convert CSV to Blob
        const blob: Blob = new Blob([csv], { type: 'text/csv' });
        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${filename}.csv`);
        // Append to html link element page
        document.body.appendChild(link);
        // Start download
        link.click();
        // Clean up and remove the link
        link.parentNode && link.parentNode.removeChild(link);
    } else if (format === 'excel') {
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet([]);
        XLSX.utils.sheet_add_aoa(worksheet, [transformedData.headers.map(header => header.label)]);
        XLSX.utils.sheet_add_json(worksheet, transformedData.rows, {
            origin: 'A2',
            skipHeader: true,
            header: transformedData.headers.map(header => header.value),
        });
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Transactions');
        XLSX.writeFile(workbook, `${filename}.xlsx`);
    }
};

const SextforceMetricsCampaignSubscribedAtGraph = (props: Props) => {
    const {
        metricId,
        promoCampaignSubscribedAtVsSales,
        promoCampaignSubscribedAtVsSalesLoading,
        showEarnings,
        showEarningsAsGross,
        showEarningsWithSubscriptions,
        timezone,
        theme,
    } = props;

    const [dataMain, setDataMain] = useState<{ data: ChartDataPoint[] }>({ data: [] });
    const [viewByMonth, setViewByMonth] = useState<boolean>(false);
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('sm'));

    // Memoize data transformation to prevent unnecessary recalculations
    // Memoize data transformation to prevent unnecessary recalculations
    const memoizedData = useMemo(
        () => generateData(promoCampaignSubscribedAtVsSales, showEarningsWithSubscriptions, showEarningsAsGross, viewByMonth, timezone),
        [promoCampaignSubscribedAtVsSales, showEarningsWithSubscriptions, showEarningsAsGross, viewByMonth, timezone],
    );

    useEffect(() => {
        if (!promoCampaignSubscribedAtVsSalesLoading && promoCampaignSubscribedAtVsSales && promoCampaignSubscribedAtVsSales.length > 0) {
            setDataMain(memoizedData);
        } else {
            setDataMain({ data: [] });
        }
    }, [promoCampaignSubscribedAtVsSalesLoading, memoizedData]);

    // Toggle between day and month view
    const handleViewToggle = (event: React.MouseEvent<HTMLElement>, newView: string) => {
        setViewByMonth(newView === 'month');
    };

    // Handle CSV download
    const handleDownloadReportCsv = () => {
        exportData(promoCampaignSubscribedAtVsSales, metricId, timezone, 'csv');
    };

    // Handle Excel download
    const handleDownloadReportExcel = () => {
        exportData(promoCampaignSubscribedAtVsSales, metricId, timezone, 'excel');
    };

    const Chart = (chartData: { data: ChartDataPoint[] }) => (
        <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
                data={chartData && chartData.data ? chartData.data : []}
                margin={{
                    top: 0,
                    right: isLargeScreen ? 0 : 0,
                    left: isLargeScreen ? -20 : -20,
                    bottom: 60,
                }}
                aria-label="Subscribers and sales chart"
            >
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.grey[400]} />
                <XAxis
                    dataKey="time"
                    name="Time"
                    tickFormatter={value => {
                        if (!value || value === 'auto') return '';

                        // If we're in month view, format as month year
                        if (viewByMonth) {
                            return moment(value).tz(timezone).format('MMM YYYY');
                        }

                        // Otherwise format as date
                        return moment(value).tz(timezone).format('L');
                    }}
                    domain={chartData && chartData.data && chartData.data.length > 0 ? ['dataMin', 'dataMax'] : ['auto', 'auto']}
                    interval={viewByMonth ? 0 : 'preserveStartEnd'} // Show all months when in month view
                    tick={{ fontSize: 13 }}
                    angle={viewByMonth ? -45 : -90}
                    dy={viewByMonth ? 20 : 35}
                    dx={viewByMonth ? 0 : -5}
                    // label={{ value: viewByMonth ? 'Month' : 'Date', position: 'insideBottom', dy: 50 }}
                />
                <YAxis
                    yAxisId="count"
                    dataKey={'count'}
                    scale={'linear'}
                    name="Subscribers Count"
                    domain={[0, (dataMax: number) => Math.abs(dataMax * 1.1) || 1]} // Prevent NaN with || 1
                    tickFormatter={(value: number) => value.toLocaleString()}
                    tick={{ fill: theme.palette.primary.main, fontSize: 13 }}
                    // label={{ value: 'Subscribers', angle: -90, position: 'insideLeft', fontSize: 11 }}
                />
                <Bar
                    yAxisId="count"
                    type="monotone"
                    dataKey="count"
                    barSize={viewByMonth ? 40 : 20}
                    maxBarSize={viewByMonth ? 40 : 20}
                    fill={theme.palette.primary.main}
                    aria-label="Subscriber count bars"
                    isAnimationActive={false}
                />
                {showEarnings && (
                    <YAxis
                        yAxisId="total"
                        dataKey={'total'}
                        scale={'linear'}
                        name="Total Sales"
                        domain={['dataMin', (dataMax: number) => Math.abs(dataMax * 1.1) || 1]} // Prevent NaN with || 1
                        tickFormatter={(value: number) =>
                            dinero({
                                amount: value && value !== Infinity ? Math.trunc(value * 100) : 0,
                                currency: 'USD',
                            }).toFormat()
                        }
                        orientation="right"
                        tick={{ fill: theme.palette.secondary.main, fontSize: 13 }}
                        // label={{ value: 'Sales', angle: 90, position: 'insideRight', fontSize: 11 }}
                    />
                )}
                {showEarnings && (
                    <Line
                        yAxisId="total"
                        type="linear"
                        dataKey="total"
                        stroke={theme.palette.secondary.main}
                        activeDot={{ r: 0 }}
                        dot={{ r: 0 }}
                        aria-label="Total sales line"
                        isAnimationActive={false}
                    />
                )}
                <Tooltip
                    content={
                        <CustomTooltip
                            showEarningsAsGross={showEarningsAsGross}
                            showEarningsWithSubscriptions={showEarningsWithSubscriptions}
                            timezone={timezone}
                            theme={theme}
                        />
                    }
                />
                <Legend
                    verticalAlign="top"
                    height={36}
                    formatter={(value: string) => (
                        <span style={{ fontSize: 13 }}>{value === 'count' ? 'Subscribers Count' : 'Total Sales'}</span>
                    )}
                />
            </ComposedChart>
        </ResponsiveContainer>
    );

    return (
        <Card sx={{ width: '100%', marginBottom: 2 }}>
            <CardContent>
                <Stack direction="column" spacing={1} sx={{ marginBottom: 1 }}>
                    <Stack direction="row" spacing={1} alignItems={'center'} justifyContent={'space-between'}>
                        <Typography variant="h5" sx={{ marginBottom: 1 }}>
                            Subscriptions Count{showEarnings ? <span> vs. Sales by Date</span> : null}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems={'center'}>
                            <MuiTooltip title="Download as CSV">
                                <span>
                                    <IconButton
                                        disabled={promoCampaignSubscribedAtVsSalesLoading}
                                        onClick={handleDownloadReportCsv}
                                        aria-label="Download as CSV"
                                    >
                                        <FontAwesomeIcon icon={faFileCsv} size="2x" color={theme.palette.info.main} />
                                    </IconButton>
                                </span>
                            </MuiTooltip>
                            <MuiTooltip title="Download as Excel">
                                <span>
                                    <IconButton
                                        disabled={promoCampaignSubscribedAtVsSalesLoading}
                                        onClick={handleDownloadReportExcel}
                                        aria-label="Download as Excel"
                                    >
                                        <FontAwesomeIcon icon={faFileExcel} size="2x" color={theme.palette.info.main} />
                                    </IconButton>
                                </span>
                            </MuiTooltip>
                        </Stack>
                    </Stack>
                    <Typography variant="body1" gutterBottom>
                        The following graph shows how many fans subscribed via this campaign on each date/month
                        {showEarnings ? <span>, and how much money these fans have spent since.</span> : null}
                    </Typography>
                    {showEarnings && (
                        <Typography variant="body1" gutterBottom>
                            Use this graph to identify when the spenders subscribed vs. when the freeloaders came along.
                        </Typography>
                    )}
                    <ToggleButtonGroup
                        value={viewByMonth ? 'month' : 'day'}
                        exclusive
                        onChange={handleViewToggle}
                        aria-label="View by"
                        size="small"
                        fullWidth={!isLargeScreen}
                    >
                        <ToggleButton value="day" aria-label="Day view">
                            <Typography variant="body2">Day View</Typography>
                        </ToggleButton>
                        <ToggleButton value="month" aria-label="Month view">
                            <Typography variant="body2">Month View</Typography>
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Stack>

                <Grid container flexGrow={1} spacing={0} sx={{ padding: 0 }}>
                    <Grid item xs={12} sx={{ height: { xs: 300, sm: 500 } }} role="figure" aria-label="Subscription data chart">
                        {promoCampaignSubscribedAtVsSalesLoading ? <Skeleton /> : Chart(dataMain)}
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

export default SextforceMetricsCampaignSubscribedAtGraph;
